import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Search, Users, Ship, Calendar, UserCheck,
  Target, Anchor, Menu, X, FileText
} from 'lucide-react';
import { useState } from 'react';
import { 
  SearchPage, 
  ActiveCrewPage, 
  AvailablePage, 
  ReliefPlansPage,
  DashboardPage,
  VesselsPage,
  CrewManagementPage,
  AssignmentWorkflowPage,
  ReportsPage
} from './pages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Navigation items
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/', label: 'AI Search', icon: Search },
  { path: '/crew', label: 'Crew', icon: Users },
  { path: '/vessels', label: 'Vessels', icon: Ship },
  { path: '/assignments/new', label: 'New Assignment', icon: Target },
  { path: '/relief-plans', label: 'Relief Plans', icon: Calendar },
  { path: '/reports', label: 'Reports', icon: FileText },
];

// Sidebar Navigation Component
const Sidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 80 : 260 }}
      className="fixed left-0 top-0 h-screen bg-white border-r border-gray-200 z-40 flex flex-col"
    >
      {/* Logo */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <NavLink to="/dashboard" className="flex items-center gap-3">
           
            {!isCollapsed && (
              <div className='flex flex-col items-center gap-3'>
              <div className='mb-5'>
               <img 
                 src="https://playground.cogence.ai/assets/cogence-logo.svg" 
                 alt="Cogence AI" 
                 className="sidebar-logo h-10" 
               />
             </div>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xl font-bold text-gray-900"
              >
                CrewAI
              </motion.span>
              </div>
            )}
          </NavLink>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
          >
            {isCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === '/' 
            ? location.pathname === '/' 
            : location.pathname.startsWith(item.path);

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? 'bg-blue-50 text-blue-600 font-medium'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
              {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="whitespace-nowrap"
                >
                  {item.label}
                </motion.span>
              )}
              {isActive && !isCollapsed && (
                <motion.div
                  layoutId="activeIndicator"
                  className="ml-auto w-1.5 h-1.5 bg-blue-600 rounded-full"
                />
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Quick Links */}
      {!isCollapsed && (
        <div className="p-4 border-t border-gray-100">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Quick Links
          </div>
          <div className="space-y-1">
            <NavLink
              to="/active-crew"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50"
            >
              <Users className="w-4 h-4 text-gray-400" />
              Active Crew
            </NavLink>
            <NavLink
              to="/available"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50"
            >
              <UserCheck className="w-4 h-4 text-gray-400" />
              Available Crew
            </NavLink>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-gray-100">
        {!isCollapsed && (
          <div className="text-xs text-gray-400">
            Enterprise Crew Management
            <br />
            Powered by AI
          </div>
        )}
      </div>
    </motion.aside>
  );
};

// Top Header Component
const TopHeader = () => {
  const location = useLocation();
  
  const getPageTitle = () => {
    if (location.pathname === '/dashboard') return 'Dashboard';
    if (location.pathname === '/') return 'AI-Powered Search';
    if (location.pathname === '/crew') return 'Crew Management';
    if (location.pathname === '/vessels') return 'Vessel Management';
    if (location.pathname.includes('/assignments')) return 'Assignment Workflow';
    if (location.pathname === '/relief-plans') return 'Relief Plans';
    if (location.pathname === '/reports') return 'Reports & Downloads';
    if (location.pathname === '/active-crew') return 'Active Crew';
    if (location.pathname === '/available') return 'Available Crew';
    return 'CrewAI';
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">{getPageTitle()}</h1>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-500">
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </span>
      </div>
    </header>
  );
};

// Layout Component
const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="ml-[260px]">
        <TopHeader />
        <main className="min-h-[calc(100vh-64px)]">
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/" element={<SearchPage />} />
            <Route path="/crew" element={<CrewManagementPage />} />
            <Route path="/active-crew" element={<ActiveCrewPage />} />
            <Route path="/available" element={<AvailablePage />} />
            <Route path="/vessels" element={<VesselsPage />} />
            <Route path="/assignments/new" element={<AssignmentWorkflowPage />} />
            <Route path="/relief-plans" element={<ReliefPlansPage />} />
            <Route path="/reports" element={<ReportsPage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#333',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
